import { inject, Injectable } from '@angular/core';
import {
  AddOrderComplete,
  BrowserInfo,
  LOGGEDINUSER,
  NavigationHelper,
  SELECTEDORDER,
  User,
  UserAgentService,
  WorkflowActionNext,
} from '@lc/core';
import { filter, Observable, of, switchMap, withLatestFrom } from 'rxjs';
import { Router } from '@angular/router';
import { ActionsSubject, Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { ofType } from '@ngrx/effects';
import { OrderService } from '@lc/store';

@Injectable({
  providedIn: 'root',
})
export class GtmEventsService {
  readonly virtualPageview$: Observable<Record<string, any>>;
  readonly marketingOrderStarted$: Observable<Record<string, any>>;
  readonly marketingOrderStepChange$: Observable<Record<string, any>>;

  constructor() {
    const store = inject(Store);
    const router = inject(Router);
    const actionSubject = inject(ActionsSubject);
    const storeOrderService = inject(OrderService);
    const userAgentService = inject(UserAgentService);
    const browserInfo: BrowserInfo = userAgentService.getBrowserInfo();

    const user$: Observable<User> = store.select(LOGGEDINUSER).pipe(
      filter((user: User) => !!user?.profile),
    );
    const marketingOrder$ = store.select(SELECTEDORDER);
    const storeOrder$ = marketingOrder$.pipe(
      switchMap((marketingOrder) => (marketingOrder?.storeOrderId
        ? storeOrderService.getOrder$(marketingOrder.storeOrderId)
        : of(undefined))),
    );

    this.virtualPageview$ = NavigationHelper.urlChanged$(router)
      .pipe(
        withLatestFrom(
          user$,
          marketingOrder$,
          storeOrder$,
        ),
        map(([url, user, marketingOrder, storeOrder]) => {
          // Set the audit date
          const auditCreatedDate = user.profile.audit?.createdAt;
          const setRoundedValueAuditCreatedDate = Math.round(new Date(auditCreatedDate).getTime() / 1000);
          const setRoundNewDateValue = Math.round(new Date().getTime() / 1000);
          const auditCreatedDateTime = auditCreatedDate ? setRoundedValueAuditCreatedDate : setRoundNewDateValue;

          return {
            // Legacy Properties
            event: 'virtualPageview',
            page_path: url,
            country: 'US',
            userType: 'Agent',
            oktaID: user.oktaId,
            personMDMID: user.personMasterId || '',
            companyMDMID: user.profile.office?.company?.companyMasterId || '',
            companyName: user.profile.office?.company?.companyName || '',
            metroID: user.profile.office?.metro?.metroNumber || '',
            metroName: user.profile.office?.metro?.metroName || '',
            officeID: user.profile.office?.officeCode || '',
            officeName: user.profile.office?.address?.name || '',
            emailAddress: user.profile.preferredEmail || 'email',
            createdAt: auditCreatedDateTime,
            brand: user.profile.office?.company?.brandCode || '',

            // Amplitude Properties
            user,
            marketingOrder,
            storeOrder,
            browserInfo,
          };
        }),
      );

    this.marketingOrderStarted$ = actionSubject.pipe(
      ofType(AddOrderComplete),
      map(({ payload }) => payload),
      map((marketingOrder) => ({
        event: 'Marketing Order Started',
        marketingOrder,
      })),
    );

    this.marketingOrderStepChange$ = actionSubject.pipe(
      ofType(WorkflowActionNext),
      withLatestFrom(marketingOrder$),
      map(([, marketingOrder]) => ({
        event: 'Marketing Order Step Completed',
        marketingOrder,
      })),
    );
  }
}
